/**
 * BrandPlayEditor.js
 * Editor for Sales Studio plays built on the Image Lab engine (Website
 * Redesigner, Content Creator, Photo Upgrade, see salesPlayConfigs.js). One
 * play = up to `maxItems` items; every item is stored as a regular Image Lab
 * AI asset (`sourceMode` "website" or "websitePhoto") with its own {{image:token}}, so the
 * send pipelines render it like any other dynamic image. The `play*` / `page*`
 * fields only exist to regroup the items here and in the overview.
 */

import React, { useEffect, useMemo, useState } from "react";
import { database } from "../firebaseConfig";
import { ref, push, update, remove } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { calculateImageCredits } from "../config/creditPlans";
import { ASPECT_OPTIONS, slugifyToken, renderAssetPreview, compressBodyImage } from "./ImageLab";
import { SALES_PLAY_CONFIGS } from "./salesPlayConfigs";
import {
  GIF_MODEL_PROFILES,
  GIF_MODEL_STATS,
  MODEL_PROFILES,
  MODEL_STATS,
  overallScore,
  recommendModels,
} from "./imageModelStats";
import PlayDemo from "./PlayDemo";
import { pinDemoImage } from "./salesPlayDemo";
import "./ImageLab.css";

const profilesFor = (config) => (config.engines === "gif" ? GIF_MODEL_PROFILES : MODEL_PROFILES);
const statsFor = (config) => (config.engines === "gif" ? GIF_MODEL_STATS : MODEL_STATS);

const STUDIED_INPUTS = [
  { icon: "fa-globe", label: "Live website screenshots" },
  { icon: "fa-copyright", label: "Logo & brand colors" },
  { icon: "fa-font", label: "Typography & imagery style" },
  { icon: "fa-magnifying-glass-chart", label: "Research & SEO data" },
  { icon: "fa-note-sticky", label: "Contact notes" },
];

const VALID_ASPECTS = ASPECT_OPTIONS.map((o) => o.value);
const ASPECT_SHAPE = {
  square: { ratio: "1:1", word: "Square" },
  landscape: { ratio: "3:2", word: "Landscape" },
  portrait: { ratio: "2:3", word: "Portrait" },
};

const ExampleShot = ({ after, before, alt, ratio }) => {
  const [missing, setMissing] = useState(false);
  if (missing) {
    return (
      <span className="wr-shot wr-shot--missing" style={{ aspectRatio: ratio }}>
        <span>Example unavailable</span>
      </span>
    );
  }
  return (
    <span className={`wr-shot ${before ? "wr-shot--compare" : ""}`} style={{ aspectRatio: ratio }}>
      <img src={after} alt={alt} loading="lazy" onError={() => setMissing(true)} />
      {before && (
        <>
          <img className="wr-shotBefore" src={before} alt="" loading="lazy" />
          <span className="wr-shotTag wr-shotTag--after">After</span>
          <span className="wr-shotTag wr-shotTag--before">Before</span>
        </>
      )}
    </span>
  );
};

const RATIO = { square: "1 / 1", landscape: "3 / 2", portrait: "2 / 3" };

const byId = (list) => Object.fromEntries(list.map((x) => [x.id, x]));

let itemKeySeq = 0;
const newItem = (config, type) => {
  const t = byId(config.itemTypes)[type] || config.itemTypes[0];
  return {
    key: `i${Date.now().toString(36)}${itemKeySeq++}`,
    assetId: null,
    type: t.id,
    label: t.label,
    labelTouched: false,
    target: config.hasTarget ? t.target || "" : "",
    brief: config.perItemDirection || config.smartPieces ? openingDirection(t.directions)?.text || "" : "",
    calculate: !!(config.perItemDirection || config.smartPieces) && !!openingDirection(t.directions)?.calculates,
    aspect: t.aspect || config.defaultAspect,
    aspectTouched: false,
    castBrand: false,
    castPhotos: [],
    castScene: {},
    look: config.looks?.[0]?.id || null,
    pinned: null,
  };
};

const CAST_MAX_PHOTOS = 3;
const CAST_ROLES = [
  { id: "me", label: "That's me" },
  { id: "product", label: "Our product" },
];
const CAST_TYPES = ["image/jpeg", "image/png", "image/webp"];
// What we look for on each contact's website. Branding is its own field
// (gifBrand); the rest is the scene scout (gifScene.js).
const CAST_THEIRS = [
  { id: "brand", label: "Their branding", icon: "fa-palette" },
  { id: "building", label: "Their building", icon: "fa-building" },
  { id: "town", label: "Their town", icon: "fa-map-location-dot" },
  { id: "local", label: "Typical details", icon: "fa-mountain-sun" },
];
const SCENE_SLOTS = ["building", "town", "local"];

const sceneOf = (item) => SCENE_SLOTS.filter((s) => item.castScene?.[s] === true);
const theirsOn = (item, id) => (id === "brand" ? !!item.castBrand : item.castScene?.[id] === true);

// Mirrors gifCast.js: an image model draws the opening frame from the cast,
// and the scene scout is one photo pick on their site.
const CAST_STARTER_MODEL = "gemini-3.1-flash-lite-image";
const castCredits = (item) => {
  const cast = castOf(item);
  const scene = sceneOf(item).length > 0;
  if (!cast.brand && !scene && !(item.castPhotos || []).length) return 0;
  const scout = scene
    ? calculateImageCredits({ model: CAST_STARTER_MODEL, aspect: item.aspect, sourceMode: "websitePhoto" }) -
      calculateImageCredits({ model: CAST_STARTER_MODEL, aspect: item.aspect })
    : 0;
  return (
    scout +
    calculateImageCredits({
      model: CAST_STARTER_MODEL,
      aspect: item.aspect,
      hasInputImage: true,
      sourceMode: cast.brand ? "website" : null,
    })
  );
};

const castOf = (item) => ({
  brand: !!item.castBrand,
  me: (item.castPhotos || []).some((p) => p.role === "me"),
  product: (item.castPhotos || []).some((p) => p.role === "product"),
});

const castFields = (item) => ({
  gifBrand: !!item.castBrand,
  gifPhotos: (item.castPhotos || []).map(({ url, storagePath, role }) => ({ url, storagePath, role })),
  gifScene: Object.fromEntries(sceneOf(item).map((s) => [s, true])),
});

const emptyPlay = (config) => ({
  name: config.defaultName,
  prefix: config.defaultPrefix,
  prefixTouched: false,
  style: config.styles[0]?.id || null,
  frame: config.frames[0].id,
  direction: config.defaultDirection,
  model: config.defaultModel,
  aspect: config.defaultAspect,
  maxWidth: displayWidthOf(config, config.maxWidth),
  align: alignOf(config, config.defaultAlign),
  useContext: true,
  keepDemo: false,
  items: config.defaultItems.map((type) => newItem(config, type)),
});

const playFromAssets = (config, groupAssets) => {
  const types = byId(config.itemTypes);
  const sorted = [...groupAssets].sort((a, b) => (a.pageOrder || 0) - (b.pageOrder || 0));
  const first = sorted[0] || {};
  return {
    name: first.playName || config.defaultName,
    prefix: first.playPrefix || config.defaultPrefix,
    prefixTouched: true,
    style: config.styles.some((s) => s.id === first.playStyle) ? first.playStyle : config.styles[0]?.id || null,
    frame: config.frames.some((f) => f.id === first.playFrame) ? first.playFrame : config.frames[0].id,
    direction: first.playDirection ?? config.defaultDirection,
    model: profilesFor(config).some((p) => p.id === first.model) ? first.model : config.defaultModel,
    aspect: first.aspect || config.defaultAspect,
    maxWidth: displayWidthOf(config, first.maxWidth ?? config.maxWidth),
    align: alignOf(config, first.align),
    useContext: first.gifUseContext !== false,
    keepDemo: sorted.some((a) => !!a.pinnedImageUrl),
    items: sorted.map((a) => {
      const takes = takesFromAsset(a);
      const selectedTakeId = takes.some((t) => t.id === a.gifSelectedTake) ? a.gifSelectedTake : takes[0]?.id || null;
      const selected = takes.find((t) => t.id === selectedTakeId) || null;
      return {
      key: a.id,
      assetId: a.id,
      type: types[a.pageType] ? a.pageType : "custom",
      label: a.pageLabel || "",
      labelTouched: true,
      target: a.pageTarget || (config.hasTarget ? (types[a.pageType] || types.custom).target : "") || "",
      brief:
        a.pageBrief ||
        (config.perItemDirection || config.smartPieces
          ? openingDirection((types[a.pageType] || types.custom).directions)?.text ||
            (types[a.pageType] || types.custom).brief ||
            ""
          : ""),
      calculate: a.pageCalculate === true,
      aspect: a.aspect || config.defaultAspect,
      aspectTouched: true,
      castBrand: a.gifBrand === true,
      castPhotos: Array.isArray(a.gifPhotos) ? a.gifPhotos : [],
      castScene: a.gifScene && typeof a.gifScene === "object" ? a.gifScene : {},
      look: config.looks?.some((l) => l.id === a.gifLook) ? a.gifLook : config.looks?.[0]?.id || null,
      pinned: a.pinnedImageUrl
        ? { imageUrl: a.pinnedImageUrl, storagePath: a.pinnedStoragePath || "", forName: a.pinnedFor || "" }
        : null,
      takes,
      selectedTakeId,
      demo: selected
        ? {
            imageUrl: selected.imageUrl,
            storagePath: selected.sourcePath || "",
            forName: selected.forName || "",
            preview: selected.storagePath
              ? { imageUrl: selected.imageUrl, storagePath: selected.storagePath }
              : null,
          }
        : null,
      token: a.token,
    };
    }),
  };
};

// Stable tokens: items that already exist keep theirs, new items get
// `<prefix>_<type>` with a numeric suffix on collision.
const assignTokens = (config, play, takenTokens) => {
  const taken = new Set(takenTokens);
  const prefix = slugifyToken(play.prefix) || config.defaultPrefix;
  return play.items.map((item) => {
    if (item.token && !taken.has(item.token)) {
      taken.add(item.token);
      return item.token;
    }
    const base =
      slugifyToken(`${prefix}_${item.type === "custom" ? item.label || "item" : item.type}`) || `${prefix}_item`;
    let token = base;
    let n = 2;
    while (taken.has(token)) token = `${base}_${n++}`;
    taken.add(token);
    return token;
  });
};

const itemTarget = (config, item) => {
  const type = byId(config.itemTypes)[item.type] || byId(config.itemTypes).custom;
  return (item.target || "").trim() || type.target || "";
};

const lookOf = (config, item) =>
  config.looks ? config.looks.find((l) => l.id === item.look) || config.looks[0] : null;

const buildItemPrompt = (config, play, item) => {
  const type = byId(config.itemTypes)[item.type] || byId(config.itemTypes).custom;
  const label = (item.label || type.label).trim();
  return config
    .buildPrompt({
      label,
      target: itemTarget(config, item) || label,
      brief: (item.brief || "").trim() || type.brief || label,
      calculate: !!item.calculate,
      guard: type.guard || "",
      format: type.format || "",
      style: byId(config.styles)[play.style] || config.styles[0],
      frame: byId(config.frames)[play.frame] || config.frames[0],
      direction: (play.direction || "").trim(),
      cast: castOf(item),
      look: lookOf(config, item),
    })
    .filter(Boolean)
    .join("\n\n");
};

const itemAspect = (config, play, item) => {
  const aspect = config.perItemAspect ? item.aspect : play.aspect;
  return VALID_ASPECTS.includes(aspect) ? aspect : config.defaultAspect;
};

const iconClass = (type) => `${type.iconStyle || "fa-solid"} ${type.icon}`;

const exampleSrc = (config, scenarioId, name) =>
  config.exampleBase && scenarioId ? `${config.exampleBase}/${scenarioId}/${name}.${config.exampleExt || "webp"}` : null;

const lookSrc = (config, typeId, lookId) =>
  config.lookShots ? exampleSrc(config, `looks/${typeId}`, lookId) : null;

// A gag can carry its own example (the photo-of-you demos). The opening gag is
// the first one without that, so a new GIF still starts on a product gag.
const openingDirection = (directions) =>
  (directions || []).find((d) => !d.shot) || (directions || [])[0] || null;

const formatCredits = (v) => (v == null || Number.isNaN(v) ? "—" : `${Number(v.toFixed(2))}`);

// Play-level email width. Only plays with `displayWidth` expose the slider;
// everyone else keeps the fixed config width.
const ALIGNS = ["left", "center", "right"];
const alignOf = (config, raw) => (ALIGNS.includes(raw) ? raw : (config.defaultAlign || "center"));
const TAKE_LIMIT = 3;

const asTake = (raw, index = 0) =>
  raw && raw.imageUrl
    ? {
        id: String(raw.id || `saved_${index}`),
        imageUrl: raw.imageUrl,
        storagePath: raw.storagePath || "",
        sourcePath: raw.sourcePath || "",
        forName: raw.forName || "",
        at: raw.at || 0,
      }
    : null;

const takesFromAsset = (asset) => {
  const stored = (Array.isArray(asset.gifTakes) ? asset.gifTakes : []).map(asTake).filter(Boolean).slice(0, TAKE_LIMIT);
  if (stored.length) return stored;
  const url = asset.previewImageUrl || asset.pinnedImageUrl;
  if (!url) return [];
  return [
    asTake({
      id: "saved",
      imageUrl: url,
      storagePath: asset.previewStoragePath || asset.pinnedStoragePath || "",
      forName: asset.pinnedFor || "",
      at: asset.previewUpdatedAt || 0,
    }),
  ];
};

const selectedTakeOf = (item) =>
  (item.takes || []).find((t) => t.id === item.selectedTakeId) || (item.takes || [])[0] || null;

const storagePathFromUrl = (url) => {
  const match = String(url || "").match(/\/o\/([^?]+)/);
  if (!match) return "";
  try {
    return decodeURIComponent(match[1]);
  } catch (_) {
    return "";
  }
};

const keepPaths = (item) => {
  const take = selectedTakeOf(item);
  return [
    take?.sourcePath,
    take?.storagePath,
    item.demo?.storagePath,
    item.demo?.preview?.storagePath,
    item.pinned?.storagePath,
    storagePathFromUrl(take?.imageUrl),
    storagePathFromUrl(item.demo?.imageUrl),
    storagePathFromUrl(item.demo?.preview?.imageUrl),
    storagePathFromUrl(item.pinned?.imageUrl),
  ].filter((p) => p && String(p).includes("email_body_images/"));
};

const displayWidthOf = (config, raw) => {
  const range = config.displayWidth;
  const fallback = config.maxWidth || 600;
  if (!range) return fallback;
  const n = parseInt(raw, 10);
  const step = range.step || 1;
  const base = Number.isFinite(n) ? n : fallback;
  const stepped = Math.round(base / step) * step;
  return Math.min(range.max, Math.max(range.min, stepped));
};

const BrandPlayEditor = ({ playType, userId, groupId = null, assets = [], onExit }) => {
  const config = SALES_PLAY_CONFIGS[playType];
  const sourceMode = config.sourceMode || "website";
  const studiedInputs = config.studiedInputs || STUDIED_INPUTS;
  const types = useMemo(() => byId(config.itemTypes), [config]);
  const scenarios = config.examples?.scenarios || [];
  const groupAssets = useMemo(
    () => (groupId ? assets.filter((a) => a.playGroupId === groupId) : []),
    [assets, groupId],
  );
  const [play, setPlay] = useState(() =>
    groupAssets.length ? playFromAssets(config, groupAssets) : emptyPlay(config),
  );
  const [scenarioId, setScenarioId] = useState(null);
  const scenario =
    scenarios.find((s) => s.id === scenarioId) ||
    scenarios.find((s) => play.items.some((i) => i.type === s.type)) ||
    scenarios[0] ||
    null;
  const scenarioDirection = scenario
    ? (types[scenario.type]?.directions || []).find((d) => d.id === scenario.engineDirection)
    : null;
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const otherTokens = useMemo(
    () =>
      assets
        .filter((a) => !groupId || a.playGroupId !== groupId)
        .map((a) => a.token)
        .filter(Boolean),
    [assets, groupId],
  );
  const tokens = useMemo(() => assignTokens(config, play, otherTokens), [config, play, otherTokens]);
  const hasReferencePhotos = config.engines === "gif" && play.items.some((item) => (item.castPhotos || []).length > 0);

  useEffect(() => {
    if (!hasReferencePhotos) return;
    const current = profilesFor(config).find((p) => p.id === play.model);
    if (current?.references) return;
    const fallback = profilesFor(config).find((p) => p.references);
    if (fallback) set({ model: fallback.id });
  }, [hasReferencePhotos, config, play.model]);

  const set = (patch) => setPlay((prev) => ({ ...prev, ...patch }));
  const setItem = (key, patch) =>
    setPlay((prev) => ({ ...prev, items: prev.items.map((i) => (i.key === key ? { ...i, ...patch } : i)) }));

  // A chart that has to compute a figure needs a model that actually reads
  // numbers. Weaker engines get replaced by the one this play names for that.
  const preferCalculationModel = () => {
    if (!config.calculationModel) return;
    const current = profilesFor(config).find((p) => p.id === play.model);
    if ((current?.stats.text || 0) < 8) set({ model: config.calculationModel });
  };

  const handleNameChange = (name) =>
    setPlay((prev) => ({
      ...prev,
      name,
      prefix: prev.prefixTouched ? prev.prefix : slugifyToken(name) || config.defaultPrefix,
    }));

  const handleTypeChange = (item, typeId) => {
    const t = types[typeId];
    const oldType = types[item.type];
    const briefIsSuggestion =
      !item.brief.trim() ||
      ((oldType?.directions || []).some((d) => d.text === item.brief.trim()) &&
        !(t?.directions || []).some((d) => d.text === item.brief.trim()));
    const targetIsDefault = !item.target.trim() || item.target.trim() === (oldType?.target || "").trim();
    setItem(item.key, {
      type: typeId,
      label: item.labelTouched ? item.label : t?.label || item.label,
      aspect: item.aspectTouched ? item.aspect : t?.aspect || item.aspect,
      ...(config.hasTarget && targetIsDefault ? { target: t?.target || "" } : {}),
      ...((config.perItemDirection || config.smartPieces) && briefIsSuggestion
        ? { brief: openingDirection(t?.directions)?.text || "" }
        : {}),
    });
  };

  const addItem = () => {
    if (play.items.length >= config.maxItems) return;
    const used = new Set(play.items.map((i) => i.type));
    const next = config.itemTypes.find((t) => t.id !== "custom" && !used.has(t.id))?.id || "custom";
    set({ items: [...play.items, newItem(config, next)] });
  };

  const removeItem = (key) => {
    if (play.items.length <= 1) return;
    set({ items: play.items.filter((i) => i.key !== key) });
  };

  const [castUploading, setCastUploading] = useState(null);
  const patchCast = (key, fn) =>
    setPlay((prev) => ({
      ...prev,
      items: prev.items.map((i) => (i.key === key ? { ...i, castPhotos: fn(i.castPhotos || []) } : i)),
    }));

  const addCastPhoto = async (item, file) => {
    if (!file) return;
    if (!CAST_TYPES.includes(file.type)) {
      setMessage("Please use a JPG, PNG or WebP photo.");
      return;
    }
    if ((item.castPhotos || []).length >= CAST_MAX_PHOTOS) return;
    try {
      setCastUploading(item.key);
      const small = await compressBodyImage(file);
      const safeName = (small.name || "photo").replace(/[^a-zA-Z0-9.]/g, "_");
      const storagePath = `email_body_images/${userId}/gif_cast/${Date.now()}_${safeName}`;
      const fileRef = storageRef(getStorage(), storagePath);
      await uploadBytes(fileRef, small);
      const url = await getDownloadURL(fileRef);
      const role = (item.castPhotos || []).some((p) => p.role === "me") ? "product" : "me";
      patchCast(item.key, (photos) => [...photos, { url, storagePath, role }].slice(0, CAST_MAX_PHOTOS));
    } catch (error) {
      setMessage("Upload failed: " + (error?.message || error));
    } finally {
      setCastUploading(null);
    }
  };

  const activeLook = (item) => {
    const list = types[item.type]?.directions || [];
    return list.find((d) => d.text === item.brief.trim()) || list[0] || null;
  };

  const pieceShot = (item) => {
    const type = types[item.type];
    const lookItem = (type?.directions || []).length ? item : play.items.find((i) => (types[i.type]?.directions || []).length);
    const lookOf = lookItem && activeLook(lookItem);
    if (config.lookShots && lookOf) return lookOf.shot || lookSrc(config, lookItem.type, lookOf.id);
    return scenario ? exampleSrc(config, scenario.id, `style-${play.style || config.styles[0]?.id}`) : null;
  };

  const photoShots = (item) => {
    const type = types[item.type];
    const folder = type?.exampleScenario || scenario?.id;
    const look = type?.exampleScenario ? activeLook(item) : null;
    return {
      before: exampleSrc(config, folder, "before"),
      after: exampleSrc(config, folder, `look-${look ? look.id : scenario?.engineDirection}`),
    };
  };

  const renderExample = (frameId, piece) => {
    const item = (piece && play.items.find((i) => i.key === piece.key)) || play.items[0];
    if (config.perItemDirection) {
      const { before, after } = photoShots(item);
      const sideBySide = frameId !== "after";
      return (
        <span className={`wr-mailPics ${sideBySide ? "" : "is-single"}`}>
          {sideBySide && (
            <span className="wr-mailPic">
              <img src={before} alt="" />
              <span>Before</span>
            </span>
          )}
          <span className="wr-mailPic">
            <img src={after} alt="" />
            {sideBySide && <span>After</span>}
          </span>
        </span>
      );
    }
    const shot = pieceShot(item);
    if (config.preview === "site") {
      const browser = (
        <span className="wr-browser">
          <span className="wr-browserBar">
            <i />
            <i />
            <i />
          </span>
          <img src={shot} alt="" />
        </span>
      );
      if (frameId === "flat") return <img className="wr-demoShot" src={shot} alt="" />;
      if (frameId === "devices") {
        return (
          <span className="wr-devices">
            {browser}
            <span className="wr-phone wr-phone--overlap">
              <img src={shot} alt="" />
            </span>
          </span>
        );
      }
      return browser;
    }
    if (frameId === "phone") {
      return (
        <span className="wr-phone">
          <img src={shot} alt="" />
        </span>
      );
    }
    if (frameId === "feed") {
      return (
        <span className="wr-feed">
          <span className="wr-feedHead">
            <span className="wr-mailAvatar" />
            <b>Their account</b>
          </span>
          <img src={shot} alt="" />
          <span className="wr-feedFoot">
            <i className="fa-regular fa-heart" /> A short caption in their voice
          </span>
        </span>
      );
    }
    return <img className="wr-demoShot" src={shot} alt="" />;
  };

  const creditsByModel = Object.fromEntries(
    profilesFor(config).map((profile) => [
      profile.id,
      play.items.reduce(
        (sum, item) =>
          item.pinned
            ? sum
            : sum +
          (calculateImageCredits({
            model: profile.id,
            aspect: itemAspect(config, play, item),
            quality: "high",
            sourceMode,
            hasInputImage: true,
            promptChars: buildItemPrompt(config, play, item).length,
          }) || 0) +
          (config.cast ? castCredits(play.useContext === false ? { ...item, castBrand: false, castScene: {} } : item) : 0),
        0,
      ),
    ]),
  );
  const totalCredits = creditsByModel[play.model] || 0;
  const demoPieces = play.items.map((item, index) => {
    const type = types[item.type] || types.custom;
    const prompt = buildItemPrompt(config, play, item);
    const aspect = itemAspect(config, play, item);
    return {
      key: item.key,
      label: `${config.itemNoun} ${index + 1}`,
      credits:
        (calculateImageCredits({
          model: play.model,
          aspect,
          quality: "high",
          sourceMode,
          hasInputImage: true,
          promptChars: prompt.length,
        }) || 0) + (config.cast ? castCredits(play.useContext === false ? { ...item, castBrand: false, castScene: {} } : item) : 0),
      draftAsset: {
        token: tokens[index],
        prompt,
        model: play.model,
        aspect,
        sourceMode,
        ...(config.hasTarget ? { photoTarget: itemTarget(config, item), photoPageHints: type.pageHints || [] } : {}),
        ...(config.cast ? castFields(item) : {}),
        ...(config.engines === "gif" ? { gifUseContext: play.useContext !== false } : {}),
      },
      pinned: item.pinned,
      shownUrl: selectedTakeOf(item)?.imageUrl || item.demo?.imageUrl || "",
      takes: item.takes || [],
      selectedTakeId: item.selectedTakeId || null,
      fixed: !!(item.pinned && selectedTakeOf(item)?.imageUrl === item.pinned.imageUrl),
      perContact: config.cast && play.useContext !== false && (!!item.castBrand || sceneOf(item).length > 0),
    };
  });
  const { bestId, valueId } = recommendModels(profilesFor(config), config.modelFocus, creditsByModel);
  const noun = config.itemNoun.toLowerCase();

  const rememberDemo = async (shot) => {
    let stored = null;
    if (shot.storagePath) {
      try {
        stored = await pinDemoImage({ userId, storagePath: shot.storagePath });
      } catch (err) {
        setMessage(err.message || "The demo could not be stored for the preview.");
      }
    }
    const take = asTake({
      id: `t_${Date.now().toString(36)}`,
      imageUrl: stored?.imageUrl || shot.imageUrl,
      storagePath: stored?.storagePath || "",
      sourcePath: shot.storagePath || "",
      forName: shot.forName || "",
      at: Date.now(),
    });
    setPlay((prev) => ({
      ...prev,
      items: prev.items.map((i) => {
        if (i.key !== shot.key || !take) return i;
        const takes = [take, ...(i.takes || []).filter((t) => t.imageUrl !== take.imageUrl)].slice(0, TAKE_LIMIT);
        const preview = take.storagePath ? { imageUrl: take.imageUrl, storagePath: take.storagePath } : null;
        return {
          ...i,
          takes,
          selectedTakeId: take.id,
          demo: {
            imageUrl: shot.imageUrl,
            storagePath: shot.storagePath || "",
            forName: shot.forName || "",
            preview,
          },
        };
      }),
    }));
  };

  const selectTake = (key, id) => {
    setPlay((prev) => ({
      ...prev,
      items: prev.items.map((i) => {
        if (i.key !== key) return i;
        const take = (i.takes || []).find((t) => t.id === id);
        if (!take) return i;
        return {
          ...i,
          selectedTakeId: id,
          demo: {
            imageUrl: take.imageUrl,
            storagePath: take.sourcePath || take.storagePath || "",
            forName: take.forName || "",
            preview: take.storagePath || take.sourcePath
              ? { imageUrl: take.imageUrl, storagePath: take.storagePath || take.sourcePath }
              : null,
          },
        };
      }),
    }));
  };

  const durableShot = (item) => {
    const preview = item.demo?.preview;
    if (preview?.imageUrl && String(preview.storagePath || "").includes("/pinned/")) return preview;
    if (item.pinned?.imageUrl && String(item.pinned.storagePath || "").includes("/pinned/")) return item.pinned;
    return null;
  };

  const setKeepDemo = async (on) => {
    if (!on) {
      setPlay((prev) => ({
        ...prev,
        keepDemo: false,
        items: prev.items.map((i) => ({ ...i, pinned: null })),
      }));
      return;
    }
    const targets = play.items.filter((i) => selectedTakeOf(i) || i.demo?.imageUrl || i.pinned?.imageUrl);
    if (!targets.length) {
      setMessage("Generate a demo first. The GIF on this card is the one you can keep.");
      return;
    }
    setPlay((prev) => ({ ...prev, keepDemo: true }));
    for (const item of targets) {
      const chosen = selectedTakeOf(item);
      const imageUrl = chosen?.imageUrl || item.demo?.imageUrl || item.pinned?.imageUrl || "";
      const paths = keepPaths(item);
      const already = paths.find((p) => p.includes("/pinned/"));
      const source = paths.find((p) => p.includes("/ai/"));
      let kept = already
        ? { imageUrl, storagePath: already }
        : durableShot(item);
      if (!kept && source) {
        try {
          // eslint-disable-next-line no-await-in-loop
          kept = await pinDemoImage({ userId, storagePath: source });
        } catch (err) {
          kept = { imageUrl, storagePath: source };
          setMessage("This GIF is kept from the preview you see. Save the bot so it stays.");
        }
      }
      if (!kept?.imageUrl && imageUrl) kept = { imageUrl, storagePath: paths[0] || "" };
      if (!kept?.imageUrl) {
        setMessage("Generate a demo first. The GIF on the card is the one you can keep.");
        setPlay((prev) => ({ ...prev, keepDemo: false }));
        return;
      }
      const shot = kept;
      setPlay((prev) => ({
        ...prev,
        keepDemo: true,
        items: prev.items.map((i) =>
          i.key === item.key
            ? {
                ...i,
                demo: i.demo ? { ...i.demo, preview: shot, imageUrl: i.demo.imageUrl || shot.imageUrl } : i.demo,
                pinned: {
                  imageUrl: shot.imageUrl,
                  storagePath: shot.storagePath,
                  forName: i.demo?.forName || i.pinned?.forName || "",
                },
              }
            : i,
        ),
      }));
    }
  };

  const copyAllTags = () => {
    try {
      navigator.clipboard.writeText(tokens.map((t) => `{{image:${t}}}`).join("\n"));
      setMessage("All tags copied. Paste them into your template where the images should appear.");
    } catch (_) {
      /* clipboard unavailable */
    }
  };

  const handleSave = async () => {
    const name = play.name.trim();
    if (!name) {
      setMessage("Please give the play a name.");
      return;
    }
    if (play.items.some((i) => i.type === "custom" && (!i.brief.trim() || (!config.smartPieces && !i.label.trim())))) {
      setMessage(
        config.smartPieces
          ? `Please describe every custom ${noun}.`
          : `Please name and describe every custom ${noun}.`,
      );
      return;
    }
    if (config.perItemDirection && play.items.some((i) => !i.brief.trim())) {
      setMessage(`Please give every ${noun} a direction.`);
      return;
    }
    if (config.hasTarget && play.items.some((i) => !itemTarget(config, i))) {
      setMessage(`Please say what we should find for every ${noun.toLowerCase()}.`);
      return;
    }

    const playGroupId = groupId || `${config.id}_${Date.now().toString(36)}`;
    const prefix = slugifyToken(play.prefix) || config.defaultPrefix;
    const basePath = `/World/${userId}/settings/imageLab`;
    const now = Date.now();
    const toPreview = [];
    const referenceFallback = hasReferencePhotos ? profilesFor(config).find((p) => p.references) : null;
    const modelId = referenceFallback && !profilesFor(config).find((p) => p.id === play.model)?.references
      ? referenceFallback.id
      : profilesFor(config).some((p) => p.id === play.model)
        ? play.model
        : config.defaultModel;

    try {
      setSaving(true);
      for (let i = 0; i < play.items.length; i++) {
        const item = play.items[i];
        const type = types[item.type] || types.custom;
        const label = (item.label || type.label).trim();
        const token = tokens[i];
        const payload = {
          type: "ai",
          name: `${name} · ${label}`,
          token,
          prompt: buildItemPrompt(config, play, item),
          aspect: itemAspect(config, play, item),
          quality: "high",
          model: modelId,
          altText: config.altText(label),
          maxWidth: displayWidthOf(config, play.maxWidth),
          align: alignOf(config, play.align),
          baseImageUrl: "",
          baseStoragePath: "",
          sourceMode,
          ...(config.hasTarget
            ? {
                photoTarget: itemTarget(config, item),
                photoPageHints: type.pageHints || [],
                pageTarget: (item.target || "").trim(),
              }
            : {}),
          ...(config.cast ? castFields(item) : {}),
          ...(config.looks ? { gifLook: lookOf(config, item).id } : {}),
          ...(config.pinnable
            ? (() => {
                const kept = config.engines === "gif" ? (play.keepDemo ? item.pinned : null) : item.pinned;
                return {
                  pinnedImageUrl: kept?.imageUrl || null,
                  pinnedStoragePath: kept?.storagePath || null,
                  pinnedFor: kept ? (item.demo?.forName || item.pinned?.forName || null) : null,
                };
              })()
            : {}),
          ...(config.engines === "gif"
            ? {
                gifUseContext: play.useContext !== false,
                gifTakes: (item.takes || []).slice(0, TAKE_LIMIT),
                gifSelectedTake: item.selectedTakeId || null,
              }
            : {}),
          play: config.id,
          playGroupId,
          playName: name,
          playPrefix: prefix,
          playStyle: play.style,
          playFrame: play.frame,
          playDirection: play.direction,
          pageType: item.type,
          pageLabel: label,
          pageBrief: item.brief.trim(),
          pageCalculate: !!item.calculate,
          pageOrder: i,
          updatedAt: now,
        };
        const prev = item.assetId ? groupAssets.find((a) => a.id === item.assetId) : null;
        const visualChanged =
          !prev ||
          prev.prompt !== payload.prompt ||
          prev.aspect !== payload.aspect ||
          JSON.stringify(prev.gifPhotos || []) !== JSON.stringify(payload.gifPhotos || []) ||
          JSON.stringify(prev.gifScene || {}) !== JSON.stringify(payload.gifScene || {}) ||
          (prev.pinnedImageUrl || null) !== (payload.pinnedImageUrl || null);
        if (config.engines === "gif") {
          const chosen = selectedTakeOf(item);
          const shot = chosen
            || item.demo?.preview
            || (item.demo?.imageUrl ? { imageUrl: item.demo.imageUrl, storagePath: item.demo.storagePath } : null);
          if (shot?.imageUrl) {
            payload.previewImageUrl = shot.imageUrl;
            payload.previewStoragePath = shot.storagePath || "";
            payload.previewUpdatedAt = now;
          }
          payload.previewPending = false;
        } else if (visualChanged) {
          payload.previewPending = true;
        }
        let assetId = item.assetId;
        if (assetId) {
          // eslint-disable-next-line no-await-in-loop
          await update(ref(database, `${basePath}/${assetId}`), payload);
        } else {
          const newRef = push(ref(database, basePath));
          // eslint-disable-next-line no-await-in-loop
          await update(newRef, { ...payload, createdAt: now + i });
          assetId = newRef.key;
        }
        if (config.engines !== "gif" && visualChanged) toPreview.push({ assetId, token, model: payload.model });
      }

      const keptIds = new Set(play.items.map((i) => i.assetId).filter(Boolean));
      await Promise.all(
        groupAssets.filter((a) => !keptIds.has(a.id)).map((a) => remove(ref(database, `${basePath}/${a.id}`))),
      );

      toPreview.forEach((preview) => renderAssetPreview({ userId, ...preview }));
      onExit();
    } catch (error) {
      console.error(`[BrandPlayEditor:${config.id}] save failed:`, error);
      setMessage("Error saving the play: " + (error?.message || error));
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!groupId) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete "${play.name}"? Templates using its tags will no longer show these images.`)) return;
    try {
      await Promise.all(groupAssets.map((a) => remove(ref(database, `/World/${userId}/settings/imageLab/${a.id}`))));
      onExit();
    } catch (error) {
      setMessage("Error deleting the play: " + (error?.message || error));
    }
  };

  const steps = {
    studies: (n) => (
      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">{n}</span>
          <div>
            <h3 className="wr-stepTitle">What the AI studies first</h3>
            <p className="wr-stepSub">
              {config.studyText || (
                <>
                  Per contact we capture their live site from <code>{"{{lead_website}}"}</code> and hand it to the model
                  together with everything stored on the contact.
                </>
              )}
            </p>
          </div>
        </div>
        <div className="wr-inputs">
          {studiedInputs.map((input) => (
            <span className="wr-input" key={input.label}>
              <i className={`fa-solid ${input.icon}`} />
              {input.label}
              <i className="fa-solid fa-check wr-inputCheck" />
            </span>
          ))}
        </div>
      </section>
    ),
    direction: (n) => (
      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">{n}</span>
          <div>
            <h3 className="wr-stepTitle">Direction</h3>
            <p className="wr-stepSub">Applies to every {noun} of this play.</p>
          </div>
        </div>
        <div
          className="wr-styles"
          role="radiogroup"
          aria-label="Style"
          style={{ "--wr-style-cols": Math.min(config.styles.length, 4) }}
        >
          {config.styles.map((style) => (
            <button
              type="button"
              key={style.id}
              role="radio"
              aria-checked={play.style === style.id}
              className={`wr-style ${play.style === style.id ? "active" : ""}`}
              onClick={() => set({ style: style.id })}
            >
              {style.light && (
                <span className="wr-styleLight">
                  <i className="fa-regular fa-sun" /> {style.light}
                </span>
              )}
              <span className="wr-styleLabel">{style.label}</span>
              <span className="wr-styleDesc">{style.desc}</span>
              {style.changes && (
                <span className="wr-styleFacts">
                  <span>
                    <b>Changes</b> {style.changes}
                  </span>
                  <span>
                    <b>Stays</b> {style.keeps}
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>
        <label className="il-field">
          <span className="il-fieldLabel">
            Your briefing <em>(optional)</em>
          </span>
          <textarea
            className="il-aiPromptInput"
            rows={3}
            value={play.direction}
            onChange={(e) => set({ direction: e.target.value })}
            placeholder={config.directionPlaceholder}
          />
        </label>
      </section>
    ),
    items: (n) => (
      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">{n}</span>
          <div>
            <h3 className="wr-stepTitle">
              {config.itemNounPlural}{" "}
              <span className="il-count">
                {play.items.length}/{config.maxItems}
              </span>
            </h3>
            <p className="wr-stepSub">{config.itemsHint}</p>
          </div>
        </div>
        {config.smartPieces && config.styles.length > 0 && (
          <>
            <div
              className="wr-styles"
              role="radiogroup"
              aria-label="Style"
              style={{ "--wr-style-cols": Math.min(config.styles.length, 3) }}
            >
              {config.styles.map((style) => (
                <button
                  type="button"
                  key={style.id}
                  role="radio"
                  aria-checked={play.style === style.id}
                  className={`wr-style ${play.style === style.id ? "active" : ""}`}
                  onClick={() => set({ style: style.id })}
                >
                  {scenario && (
                    <img
                      className={`wr-styleShot ${config.preview === "site" ? "wr-styleShot--land" : ""}`}
                      src={exampleSrc(config, scenario.id, `style-${style.id}`)}
                      alt=""
                    />
                  )}
                  <span className="wr-styleLabel">{style.label}</span>
                  <span className="wr-styleDesc">{style.desc}</span>
                </button>
              ))}
            </div>
            <label className="il-field wr-campaign">
              <span className="il-fieldLabel">
                {config.campaignLabel || "Campaign"} <em>({config.campaignNote || "optional, every piece"})</em>
              </span>
              <textarea
                className="il-aiPromptInput"
                rows={2}
                aria-label={config.campaignLabel || "Campaign"}
                value={play.direction}
                onChange={(e) => set({ direction: e.target.value })}
                placeholder={config.directionPlaceholder}
              />
            </label>
          </>
        )}
        <div className={`wr-pages ${config.perItemDirection || config.smartPieces ? "wr-pages--wide" : ""}`}>
          {play.items.map((item, index) => {
            const type = types[item.type] || types.custom;
            if (config.perItemDirection) {
              return (
                <div className="wr-page wr-page--flow" key={item.key}>
                  <div className="wr-pageHead">
                    <span className="wr-pageIcon">
                      <i className={iconClass(type)} aria-hidden="true" />
                    </span>
                    <span className="wr-pageNum">
                      {config.itemNoun} {index + 1}
                    </span>
                    <code className="wr-pageTag">{`{{image:${tokens[index]}}}`}</code>
                    {play.items.length > 1 && (
                      <button
                        type="button"
                        className="wr-pageRemove"
                        onClick={() => removeItem(item.key)}
                        title={`Remove ${noun}`}
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    )}
                  </div>
                  <div className="wr-photoZone">
                    <div className="wr-photoHead">
                      <span className="wr-photoKicker">On their website</span>
                      <span className="wr-photoQuestion">What should we find?</span>
                    </div>
                    <div
                      className="wr-types"
                      role="radiogroup"
                      aria-label={`What should we find for ${config.itemNoun} ${index + 1}`}
                    >
                      {config.itemTypes.map((t) => {
                        const shot = t.exampleScenario && exampleSrc(config, t.exampleScenario, "before");
                        return (
                          <button
                            type="button"
                            key={t.id}
                            role="radio"
                            aria-checked={item.type === t.id}
                            className={`wr-type wr-type--shot ${item.type === t.id ? "active" : ""}`}
                            onClick={() => handleTypeChange(item, t.id)}
                          >
                            {shot ? (
                              <img className="wr-typeShot" src={shot} alt="" />
                            ) : (
                              <span className="wr-typeShot wr-typeShot--empty" aria-hidden="true">
                                <i className={iconClass(t)} />
                              </span>
                            )}
                            <span className="wr-typeName">
                              <i className={iconClass(t)} aria-hidden="true" />
                              {t.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="wr-find">
                      <span>We'll look for</span>
                      <input
                        type="text"
                        aria-label={`We'll look for, ${config.itemNoun} ${index + 1}`}
                        value={item.target}
                        onChange={(e) => setItem(item.key, { target: e.target.value })}
                        placeholder={type.target || "e.g. a photo of their wine cellar"}
                      />
                    </div>
                  </div>
                  <div className="wr-photoZone">
                    <div className="wr-photoHead">
                      <span className="wr-photoKicker">The result</span>
                      <span className="wr-photoQuestion">What should happen to it?</span>
                      <div
                        className="wr-formats"
                        role="radiogroup"
                        aria-label={`Format for ${config.itemNoun} ${index + 1}`}
                      >
                        {ASPECT_OPTIONS.map((o) => {
                          const shape = ASPECT_SHAPE[o.value];
                          const active = item.aspect === o.value;
                          return (
                            <button
                              type="button"
                              key={o.value}
                              role="radio"
                              aria-checked={active}
                              aria-label={`${shape.word} ${shape.ratio}`}
                              className={`wr-format ${active ? "active" : ""}`}
                              onClick={() => setItem(item.key, { aspect: o.value, aspectTouched: true })}
                            >
                              <span className={`wr-formatShape wr-formatShape--${o.value}`} />
                              {shape.ratio}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div
                      className={`wr-directions ${type.exampleScenario ? "wr-directions--photo" : ""}`}
                      role="group"
                      aria-label={`Direction suggestions for ${config.itemNoun} ${index + 1}`}
                    >
                      {(type.directions || []).map((dir) => {
                        const active = item.brief.trim() === dir.text;
                        return (
                          <button
                            type="button"
                            key={dir.id}
                            aria-pressed={active}
                            className={`wr-direction ${active ? "active" : ""}`}
                            onClick={() => setItem(item.key, { brief: dir.text })}
                          >
                            {type.exampleScenario && (
                              <ExampleShot
                                after={exampleSrc(config, type.exampleScenario, `look-${dir.id}`)}
                                before={exampleSrc(config, type.exampleScenario, "before")}
                                alt={`${dir.label} example`}
                                ratio={type.aspect === "square" ? "1 / 1" : "3 / 2"}
                              />
                            )}
                            <span className="wr-directionLabel">{dir.label}</span>
                            <span className="wr-styleLight">
                              <i className="fa-regular fa-sun" /> {dir.light}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <textarea
                      className="il-aiPromptInput"
                      rows={3}
                      aria-label={`Direction for ${config.itemNoun} ${index + 1}`}
                      value={item.brief}
                      onChange={(e) => setItem(item.key, { brief: e.target.value })}
                      placeholder="Or describe it in your own words."
                    />
                    <span className="il-fieldHint">
                      An idea fills this in for you. Edit it whenever you want something more specific.
                      {type.exampleScenario && ""}
                    </span>
                  </div>
                </div>
              );
            }
            if (config.smartPieces) {
              const ideas = type.directions || [];
              const copy = config.smartCopy || {};
              const onlyType = config.itemTypes.length < 2;
              const formatControls = config.perItemAspect && (
                <div className="wr-formats" role="radiogroup" aria-label={`Format for ${config.itemNoun} ${index + 1}`}>
                  {ASPECT_OPTIONS.map((o) => {
                    const shape = ASPECT_SHAPE[o.value];
                    const active = item.aspect === o.value;
                    return (
                      <button
                        type="button"
                        key={o.value}
                        role="radio"
                        aria-checked={active}
                        aria-label={`${shape.word} ${shape.ratio}`}
                        className={`wr-format ${active ? "active" : ""}`}
                        onClick={() => setItem(item.key, { aspect: o.value, aspectTouched: true })}
                      >
                        <span className={`wr-formatShape wr-formatShape--${o.value}`} />
                        {shape.ratio}
                      </button>
                    );
                  })}
                </div>
              );
              return (
                <div className="wr-page wr-page--flow" key={item.key}>
                  <div className="wr-pageHead">
                    <span className="wr-pageIcon">
                      <i className={iconClass(type)} aria-hidden="true" />
                    </span>
                    <span className="wr-pageNum">
                      {config.itemNoun} {index + 1}
                    </span>
                    <code className="wr-pageTag">{`{{image:${tokens[index]}}}`}</code>
                    {play.items.length > 1 && (
                      <button
                        type="button"
                        className="wr-pageRemove"
                        onClick={() => removeItem(item.key)}
                        title={`Remove ${noun}`}
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    )}
                  </div>
                  {item.pinned && config.engines !== "gif" && (
                    <div className="wr-pinned" role="group" aria-label={`Used every time, ${config.itemNoun} ${index + 1}`}>
                      <img src={item.pinned.imageUrl} alt={`Fixed ${config.itemNoun} for every contact`} />
                      <span className="wr-pinnedText">
                        <strong>
                          <i className="fa-solid fa-thumbtack" aria-hidden="true" /> Used every time
                        </strong>
                        Every contact gets exactly this {config.itemNoun}
                        {item.pinned.forName ? `, made for ${item.pinned.forName}` : ""}. No new render and no credits per
                        send. The gag and cast below only count again once you switch back.
                      </span>
                      <button type="button" className="altBtn altBtn--petrol" onClick={() => setItem(item.key, { pinned: null })}>
                        <i className="fa-solid fa-wand-magic-sparkles" /> Make one per contact
                      </button>
                    </div>
                  )}
                  {!onlyType && (
                  <div className="wr-photoZone">
                    <div className="wr-photoHead">
                      <span className="wr-photoKicker">{copy.kicker || "The channel"}</span>
                      <span className="wr-photoQuestion">{copy.question || "What are we making?"}</span>
                      {formatControls}
                    </div>
                    <div
                      className="wr-types"
                      role="radiogroup"
                      aria-label={`${copy.group || "What are we making"} for ${config.itemNoun} ${index + 1}`}
                    >
                      {config.itemTypes.map((t) => (
                        <button
                          type="button"
                          key={t.id}
                          role="radio"
                          aria-checked={item.type === t.id}
                          className={`wr-type ${item.type === t.id ? "active" : ""}`}
                          onClick={() => handleTypeChange(item, t.id)}
                        >
                          <i className={iconClass(t)} aria-hidden="true" />
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  )}
                  <div className="wr-photoZone">
                    <div className="wr-photoHead">
                      <span className="wr-photoKicker">{copy.ideaKicker || "The idea"}</span>
                      <span className="wr-photoQuestion">{copy.ideaQuestion || "What should it say?"}</span>
                      {onlyType && formatControls}
                    </div>
                    {ideas.length > 0 && (
                      <div
                        className="wr-directions"
                        role="group"
                        aria-label={`Ideas for ${config.itemNoun} ${index + 1}`}
                      >
                        {ideas.map((dir) => {
                          const active = item.brief.trim() === dir.text;
                          const shot = dir.shot || lookSrc(config, type.id, dir.id);
                          return (
                            <button
                              type="button"
                              key={dir.id}
                              aria-pressed={active}
                              className={`wr-direction ${active ? "active" : ""}`}
                              onClick={() => {
                                setItem(item.key, { brief: dir.text, calculate: !!dir.calculates });
                                if (dir.calculates) preferCalculationModel();
                              }}
                            >
                              {shot && (
                                <ExampleShot
                                  after={shot}
                                  alt={`${dir.label} example`}
                                  ratio={RATIO[type.aspect] || RATIO.square}
                                />
                              )}
                              <span className="wr-directionLabel">{dir.label}</span>
                              <span className="wr-styleLight">
                                <i className={config.directionIcon || "fa-regular fa-sun"} /> {dir.light}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {copy.promptLabel && (
                      <span className="il-fieldLabel">
                        {copy.promptLabel} {copy.promptNote && <em>({copy.promptNote})</em>}
                      </span>
                    )}
                    <textarea
                      className="il-aiPromptInput"
                      rows={3}
                      aria-label={`${copy.promptLabel || "Idea"} for ${config.itemNoun} ${index + 1}`}
                      value={item.brief}
                      onChange={(e) => setItem(item.key, { brief: e.target.value })}
                      placeholder={type.brief || "Describe the piece in your own words."}
                    />
                    {config.calculation && (
                      <label className="wr-calc">
                        <input
                          type="checkbox"
                          checked={!!item.calculate}
                          onChange={(e) => {
                            setItem(item.key, { calculate: e.target.checked });
                            if (e.target.checked) preferCalculationModel();
                          }}
                        />
                        <span>
                          <strong>{config.calculation.title}</strong>
                          {config.calculation.note}
                        </span>
                      </label>
                    )}
                    <span className="il-fieldHint">
                      {item.calculate && config.calculation
                        ? config.calculation.hint
                        : copy.hint ||
                          "An idea fills this in. The piece still uses their real logo, colors and offer, and never invents a price."}
                    </span>
                  </div>
                  {config.looks && (
                    <div className="wr-photoZone">
                      <div className="wr-photoHead">
                        <span className="wr-photoKicker">The look</span>
                        <span className="wr-photoQuestion">How should it look?</span>
                      </div>
                      <div
                        className="wr-types"
                        role="radiogroup"
                        aria-label={`Look for ${config.itemNoun} ${index + 1}`}
                      >
                        {config.looks.map((l) => {
                          const on = (item.look || config.looks[0].id) === l.id;
                          return (
                            <button
                              type="button"
                              key={l.id}
                              role="radio"
                              aria-checked={on}
                              className={`wr-type ${on ? "active" : ""}`}
                              onClick={() => setItem(item.key, { look: l.id })}
                            >
                              <i className={`fa-solid ${l.icon}`} aria-hidden="true" />
                              {l.label}
                            </button>
                          );
                        })}
                      </div>
                      <span className="il-fieldHint">
                        {lookOf(config, item).prompt
                          ? `The story stays, only the style changes: ${lookOf(config, item).label.toLowerCase()}, whatever the gag text says.`
                          : "Keeps the style the gag is written in. Pick a look to render the same story differently."}
                      </span>
                    </div>
                  )}
                  {config.cast && (
                    <div className="wr-photoZone" role="group" aria-label={`Cast for ${config.itemNoun} ${index + 1}`}>
                      <div className="wr-photoHead">
                        <span className="wr-photoKicker">The cast</span>
                        <span className="wr-photoQuestion">Who's in it?</span>
                      </div>
                      <span className="il-fieldLabel">From their website</span>
                      <div
                        className="wr-types"
                        role="group"
                        aria-label={`From their website for ${config.itemNoun} ${index + 1}`}
                      >
                        {CAST_THEIRS.map((o) => {
                          const on = theirsOn(item, o.id);
                          return (
                            <button
                              type="button"
                              key={o.id}
                              role="checkbox"
                              aria-checked={on}
                              className={`wr-type ${on ? "active" : ""}`}
                              disabled={!play.useContext}
                              onClick={() =>
                                setItem(
                                  item.key,
                                  o.id === "brand"
                                    ? { castBrand: !on }
                                    : { castScene: { ...(item.castScene || {}), [o.id]: !on } },
                                )
                              }
                            >
                              <i className={`fa-solid ${o.icon}`} aria-hidden="true" />
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                      <span className="il-fieldHint">
                        {play.useContext
                          ? "Found per contact on their site: logo and colours, their building, their town and what is typical for them. Whatever we can't find is left out, and people in their photos are never used."
                          : "Turned off while the GIF uses only your prompt. Your own photos below still play along."}
                      </span>
                      <span className="il-fieldLabel">From you</span>
                      <div className="wr-cast">
                        {(item.castPhotos || []).map((photo) => (
                          <div className="wr-castPhoto" key={photo.url}>
                            <img src={photo.url} alt="" />
                            <button
                              type="button"
                              className="wr-castRemove"
                              aria-label="Remove photo"
                              onClick={() => patchCast(item.key, (photos) => photos.filter((p) => p.url !== photo.url))}
                            >
                              <i className="fa-solid fa-xmark" />
                            </button>
                            <div className="wr-castRoles" role="radiogroup" aria-label="Who is this">
                              {CAST_ROLES.map((r) => (
                                <button
                                  type="button"
                                  key={r.id}
                                  role="radio"
                                  aria-checked={photo.role === r.id}
                                  className={`wr-castRole ${photo.role === r.id ? "active" : ""}`}
                                  onClick={() =>
                                    patchCast(item.key, (photos) =>
                                      photos.map((p) => (p.url === photo.url ? { ...p, role: r.id } : p)),
                                    )
                                  }
                                >
                                  {r.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                        {(item.castPhotos || []).length < CAST_MAX_PHOTOS && (
                          <label className={`altBtn altBtn--petrol wr-castAdd ${castUploading === item.key ? "is-busy" : ""}`}>
                            <i className={`fa-solid ${castUploading === item.key ? "fa-spinner fa-spin" : "fa-camera"}`} />
                            {castUploading === item.key ? "Uploading" : "Add a photo"}
                            <input
                              type="file"
                              accept={CAST_TYPES.join(",")}
                              aria-label={`Add a photo to ${config.itemNoun} ${index + 1}`}
                              disabled={castUploading === item.key}
                              onChange={(e) => {
                                addCastPhoto(item, e.target.files?.[0]);
                                e.target.value = "";
                              }}
                            />
                          </label>
                        )}
                      </div>
                      <span className="il-fieldHint">
                        Up to {CAST_MAX_PHOTOS} photos. A photo of you becomes a cartoon you that plays along in the gag.
                        {castCredits(play.useContext === false ? { ...item, castBrand: false, castScene: {} } : item) > 0 &&
                          ` The opening frame${play.useContext !== false && sceneOf(item).length ? " and the search on their site add" : " adds"} ${formatCredits(castCredits(play.useContext === false ? { ...item, castBrand: false, castScene: {} } : item))} credits per contact.`}
                      </span>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div className="wr-page" key={item.key}>
                <div className="wr-pageFields">
                  <div className="wr-pageHead">
                    <span className="wr-pageIcon">
                      <i className={iconClass(type)} />
                    </span>
                    <span className="wr-pageNum">
                      {config.itemNoun} {index + 1}
                    </span>
                    {play.items.length > 1 && (
                      <button
                        type="button"
                        className="wr-pageRemove"
                        onClick={() => removeItem(item.key)}
                        title={`Remove ${noun}`}
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    )}
                  </div>
                  <label className="il-field">
                    <span className="il-fieldLabel">Type</span>
                    <select value={item.type} onChange={(e) => handleTypeChange(item, e.target.value)}>
                      {config.itemTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="il-field">
                    <span className="il-fieldLabel">Name</span>
                    <input
                      type="text"
                      value={item.label}
                      onChange={(e) => setItem(item.key, { label: e.target.value, labelTouched: true })}
                    />
                  </label>
                  {config.perItemAspect && (
                    <label className="il-field">
                      <span className="il-fieldLabel">Format</span>
                      <select
                        value={item.aspect}
                        onChange={(e) => setItem(item.key, { aspect: e.target.value, aspectTouched: true })}
                      >
                        {ASPECT_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label className="il-field">
                    <span className="il-fieldLabel">What should it show?</span>
                    <textarea
                      className="il-aiPromptInput"
                      rows={4}
                      value={item.brief}
                      onChange={(e) => setItem(item.key, { brief: e.target.value })}
                      placeholder={type.brief || `Describe the ${noun}.`}
                    />
                  </label>
                  <code className="wr-pageTag">{`{{image:${tokens[index]}}}`}</code>
                </div>
              </div>
            );
          })}
          {play.items.length < config.maxItems && (
            <button type="button" className="wr-pageAdd" onClick={addItem}>
              <i className="fa-solid fa-plus" />
              Add {noun}
              <span>up to {config.maxItems}</span>
            </button>
          )}
        </div>
      </section>
    ),
    output: (n) => (
      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">{n}</span>
          <div>
            <h3 className="wr-stepTitle">Output</h3>
            <p className="wr-stepSub">
              {config.perItemDirection
                ? "Name, tag, and a live test of the email for a real contact."
                : config.smartPieces
                  ? config.outputSub || "Name, tag, and how the piece shows up in the email."
                  : "How the images look inside the email."}
            </p>
          </div>
        </div>
        <div className="wr-outputGrid">
          <label className="il-field">
            <span className="il-fieldLabel">Play name</span>
            <input type="text" value={play.name} onChange={(e) => handleNameChange(e.target.value)} />
          </label>
          <label className="il-field">
            <span className="il-fieldLabel">Tag prefix</span>
            <input
              type="text"
              value={play.prefix}
              onChange={(e) => set({ prefix: slugifyToken(e.target.value), prefixTouched: true })}
              placeholder={config.defaultPrefix}
            />
          </label>
          {config.displayWidth && (
            <label className="wr-width wr-outputWide">
              <span className="wr-widthHead">
                <span className="il-fieldLabel">Width in the email</span>
                <span className="wr-widthValue">{play.maxWidth} px</span>
              </span>
              <input
                type="range"
                min={config.displayWidth.min}
                max={config.displayWidth.max}
                step={config.displayWidth.step}
                value={play.maxWidth}
                aria-label="Width in the email"
                style={{ "--fill": `${((play.maxWidth - config.displayWidth.min) / (config.displayWidth.max - config.displayWidth.min)) * 100}%` }}
                onChange={(e) => set({ maxWidth: displayWidthOf(config, e.target.value) })}
              />
              <span className="wr-widthScale">
                <span>{config.displayWidth.min} px</span>
                <span>{config.displayWidth.max} px</span>
              </span>
              <span className="wr-widthHint">How wide the GIF shows in the mail. The file stays the same size.</span>
            </label>
          )}
          {config.engines === "gif" && (
            <div className="wr-align wr-outputWide" role="radiogroup" aria-label="Position in the email">
              {[
                ["left", "Left"],
                ["center", "Center"],
                ["right", "Right"],
              ].map(([id, label]) => (
                <button
                  type="button"
                  key={id}
                  role="radio"
                  aria-checked={play.align === id}
                  className={`wr-alignBtn ${play.align === id ? "active" : ""}`}
                  onClick={() => set({ align: id })}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {config.perItemDirection || config.smartPieces ? (
            <div className="wr-outputWide">
              <PlayDemo
                userId={userId}
                config={config}
                frame={play.frame}
                onFrame={(frame) => set({ frame })}
                pieces={demoPieces}
                model={play.model}
                credits={(p) => p.credits}
                renderExample={renderExample}
                mailText={config.demoText || "I put something together for you."}
                onPin={config.pinnable && config.engines !== "gif" ? (key, pinned) => setItem(key, { pinned }) : null}
                onDemo={config.engines === "gif" ? rememberDemo : null}
                onSelectTake={config.engines === "gif" ? selectTake : null}
                onKeep={config.engines === "gif" ? setKeepDemo : null}
                footNote={
                  config.engines === "gif"
                    ? play.useContext
                      ? "The latest demo is stored and becomes the card preview when you save. This run still uses the contact."
                      : "The latest demo is stored and becomes the card preview when you save. This run uses only your prompt."
                    : ""
                }
                displayWidth={config.displayWidth ? play.maxWidth : null}
                align={config.engines === "gif" ? play.align : "center"}
              />
            </div>
          ) : (
            <label className="il-field">
              <span className="il-fieldLabel">Presentation</span>
              <select value={play.frame} onChange={(e) => set({ frame: e.target.value })}>
                {config.frames.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          {!config.perItemAspect && !config.smartPieces && (
            <label className="il-field">
              <span className="il-fieldLabel">Aspect</span>
              <select value={play.aspect} onChange={(e) => set({ aspect: e.target.value })}>
                {ASPECT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        {config.engines === "gif" && (
          <div className="wr-delivery" role="group" aria-label="How this GIF is made">
            <button
              type="button"
              role="switch"
              aria-checked={play.useContext !== false}
              aria-label="Use the contact"
              className={`wr-switch ${play.useContext !== false ? "is-on" : ""}`}
              onClick={() => set({ useContext: play.useContext === false })}
            >
              <span className="wr-switchCopy">
                <span className="wr-switchLabel">Use the contact</span>
                <span className="wr-switchHint">
                  {play.useContext !== false
                    ? "Their industry, place, research and website shape the GIF."
                    : "Only your prompt. Your own photos still play along. Their data stays out."}
                </span>
              </span>
              <span className="wr-switchKnob" aria-hidden="true" />
            </button>
          </div>
        )}
      </section>
    ),
    engine: (n) => (
      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">{n}</span>
          <div>
            <h3 className="wr-stepTitle">Engine</h3>
            <p className="wr-stepSub">
              Which AI renders the {config.engines === "gif" ? "GIFs" : "images"}. For this play what counts most is {config.modelFocusLabel}.
              {play.items.some((i) => i.calculate) && config.calculation?.engineHint
                ? ` ${config.calculation.engineHint}`
                : config.engineHint
                ? ` ${config.engineHint}`
                : config.smartPieces
                  ? " Ads, vouchers and posters need a high text score. Photo-led posts need photoreal and brand match."
                  : ""}
            </p>
          </div>
        </div>
        {scenario && (
          <div className="wr-scenarios" role="tablist" aria-label="Example scenario">
            <span className="wr-scenariosLabel">See examples for</span>
            {scenarios.map((s) => (
              <button
                type="button"
                key={s.id}
                role="tab"
                aria-selected={s.id === scenario.id}
                className={`wr-scenario ${s.id === scenario.id ? "active" : ""}`}
                onClick={() => setScenarioId(s.id)}
              >
                <i className={s.icon ? `fa-solid ${s.icon}` : iconClass(types[s.type])} /> {s.label}
              </button>
            ))}
          </div>
        )}
        {hasReferencePhotos && (
          <p className="wr-modelNote">
            {profilesFor(config)
              .filter((p) => !p.references)
              .map((p) => p.name)
              .join(", ")}{" "}
            are off. Generation with reference images does not work on them. Remove the photos to use those engines.
          </p>
        )}
        <div
          className={`wr-models ${profilesFor(config).length === 4 ? "wr-models--four" : ""}`}
          role="radiogroup"
          aria-label="Engine"
        >
          {profilesFor(config).map((profile) => {
            const blocked = hasReferencePhotos && !profile.references;
            const active = !blocked && play.model === profile.id;
            const overall = overallScore(profile, statsFor(config));
            return (
              <button
                type="button"
                key={profile.id}
                role="radio"
                aria-checked={active}
                aria-disabled={blocked}
                disabled={blocked}
                aria-label={profile.name}
                className={`wr-model ${active ? "active" : ""} ${blocked ? "is-off" : ""}`}
                onClick={() => {
                  if (!blocked) set({ model: profile.id });
                }}
              >
                {scenario && exampleSrc(config, scenario.id, `engine-${profile.id}`) && (
                  <ExampleShot
                    after={exampleSrc(config, scenario.id, `engine-${profile.id}`)}
                    before={config.exampleBefore ? exampleSrc(config, scenario.id, "before") : null}
                    alt={`${profile.name} example`}
                    ratio={config.exampleRatio}
                  />
                )}
                <span className="wr-modelHead">
                  <span className="wr-modelName">
                    {profile.name}
                    <span className="wr-modelProvider">{profile.provider}</span>
                  </span>
                  <span className="wr-modelScore" title="Overall quality, average of all stats">
                    {overall.toFixed(1)}
                  </span>
                </span>
                {(profile.id === bestId || profile.id === valueId) && (
                  <span
                    className={`wr-modelBadge ${profile.id === bestId ? "wr-modelBadge--best" : "wr-modelBadge--value"}`}
                  >
                    <i className={`fa-solid ${profile.id === bestId ? "fa-trophy" : "fa-scale-balanced"}`} />
                    {profile.id === bestId ? "Best for this play" : "Best value"}
                  </span>
                )}
                <span className="wr-modelTagline">{profile.tagline}</span>
                {config.modelFit?.[profile.id] && <span className="wr-modelFit">{config.modelFit[profile.id]}</span>}
                <span className="wr-modelStats">
                  {statsFor(config).map((stat) => {
                    const value = profile.stats[stat.id] || 0;
                    const focus = (config.modelFocus || []).includes(stat.id);
                    return (
                      <span key={stat.id} className={`wr-modelStat ${focus ? "is-focus" : ""}`}>
                        <span className="wr-modelStatLabel">{stat.label}</span>
                        <span className="wr-modelBar" aria-hidden="true">
                          <span style={{ width: `${value * 10}%` }} />
                        </span>
                        <span className="wr-modelStatValue">{value}</span>
                      </span>
                    );
                  })}
                </span>
                <span className="wr-modelCost">
                  <i className="fa-solid fa-coins" />
                  {formatCredits(creditsByModel[profile.id])} <em>credits / contact</em>
                </span>
                {blocked && (
                  <span className="wr-modelBlock">
                    Reference images don't work on this engine. Remove the photos to use it.
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <p className="wr-modelNote">
          Stats are our rating from test runs (1 to 10). Highlighted stats matter most for this play.
          {scenario &&
            (config.perItemDirection
              ? ` Every example is the same ${scenario.label.toLowerCase()} photo with "${scenarioDirection?.label}".`
              : ` Every example is the same ${scenario.label.toLowerCase()} ${
                  scenario.note ||
                  config.exampleNote ||
                  "post, so you can see who keeps the headline readable and who makes the photo look real."
                }`)}
        </p>
      </section>
    ),
  };
  const stepOrder =
    config.perItemDirection || config.smartPieces
      ? ["items", "engine", "output"]
      : ["studies", "direction", "items", "output", "engine"];

  return (
    <div className="il wr">
      <div className="il-editorBar">
        <span className="il-editorTitle">{groupId ? `Edit ${config.name}` : `New ${config.name}`}</span>
      </div>

      {stepOrder.map((key, index) => (
        <React.Fragment key={key}>{steps[key](index + 1)}</React.Fragment>
      ))}

      {message && <div className="il-msg">{message}</div>}

      <div className="il-editorActions">
        <div
          className="il-costMeter"
          title={`Credits per contact. Each ${noun} is one generated image incl. the website capture; cached images are reused for free.`}
        >
          <span className="il-costMeterIcon">
            <i className="fa-solid fa-coins" />
          </span>
          <span className="il-costMeterBody">
            <span className="il-costMeterMain">
              {formatCredits(totalCredits)} <em>credits / contact</em>
            </span>
            <span className="il-costMeterMeta">
              {play.items.length} × {noun}
              {play.items.length === 1 ? "" : "s"} ·{" "}
              {sourceMode === "websitePhoto" ? "incl. photo pick" : "incl. screenshot"}
            </span>
          </span>
        </div>
        <div className="il-editorActionsBtns">
          {groupId && (
            <button type="button" className="il-btnGhost wr-delete" onClick={handleDelete} disabled={saving}>
              <i className="fa-regular fa-trash-can" /> Delete
            </button>
          )}
          <button type="button" className="altBtn altBtn--petrol" onClick={copyAllTags}>
            <i className="fa-regular fa-copy" /> Copy tags
          </button>
          <button type="button" className="altBtn altBtn--save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save play"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BrandPlayEditor;
